import React, { useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { COUNTRIES, getCurrencySymbol, getPrefillData } from './utils';

/* ─── Industry options ─── */
const industryOptions = [
  'Apparel & Fashion',
  'Beauty & Cosmetics',
  'Footwear',
  'Jewellery',
  'Electronics',
  'Home',
  'Food & Restaurants',
  'Grocery',
];

/* ─── Format display value with commas ─── */
const formatNumber = (val) => {
  if (val === null || val === undefined || val === '') return '';
  return Number(val).toLocaleString('en-IN');
};

/* ─── Strip non-numeric chars ─── */
const stripToInt = (raw) => {
  const cleaned = raw.replace(/[^0-9]/g, '');
  return cleaned === '' ? null : parseInt(cleaned, 10);
};

const stripToFloat = (raw) => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (cleaned === '' || cleaned === '.') return null;
  return parseFloat(cleaned);
};

/* ── Glass card style (light) ── */
const glassCard = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.8)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
};

/* ─── Light Select Dropdown ─── */
const SelectDropdown = ({ value, onChange, options, placeholder, className = '' }) => (
  <div className={`relative ${className}`}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full h-11 pl-3 pr-9 text-sm font-medium border rounded-lg appearance-none focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all cursor-pointer ${
        value ? 'text-[#0f172a]' : 'text-[#94a3b8]'
      }`}
      style={{
        background: 'rgba(255,255,255,0.9)',
        borderColor: '#e2e8f0',
      }}
      data-testid={`select-${placeholder?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {placeholder && (
        <option value="" disabled style={{ color: '#94a3b8' }}>
          {placeholder}
        </option>
      )}
      {options.map((opt) => {
        const val = typeof opt === 'string' ? opt : opt.code || opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        return (
          <option key={val} value={val}>
            {label}
          </option>
        );
      })}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8] pointer-events-none" />
  </div>
);

/* ─── Light Range Slider ─── */
const RangeSlider = ({ value, onChange, min = 0, max = 100, step = 1 }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative w-full h-[20px] flex items-center">
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-[#e2e8f0]" />
      <div
        className="absolute top-1/2 -translate-y-1/2 left-0 h-1.5 rounded-full"
        style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #3b82f6, #2563eb)' }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="absolute inset-0 w-full h-full cursor-pointer"
        style={{ opacity: 0, zIndex: 2 }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white border-[3px] border-[#3b82f6] pointer-events-none"
        style={{
          left: `calc(${pct}% - 10px)`,
          boxShadow: '0 2px 6px rgba(59,130,246,0.25)',
          zIndex: 1,
        }}
      />
    </div>
  );
};

/* ──────────────────────────────────────────────
   INPUTS SCREEN — Light Theme
   ────────────────────────────────────────────── */
const InputsScreen = ({ onNext, onBack, businessInputs, setBusinessInputs }) => {
  const { monthlyOrders, aov, country, industry, dataCollectionPct, repeatSalesPct } = businessInputs;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const update = (field, value) => {
    setBusinessInputs((prev) => ({ ...prev, [field]: value }));
  };

  /* ─── Auto-prefill when country + industry change ─── */
  useEffect(() => {
    const prefill = getPrefillData(country, industry);
    if (prefill) {
      setBusinessInputs((prev) => ({
        ...prev,
        monthlyOrders: prefill.monthlyOrders,
        aov: prefill.aov,
        dataCollectionPct: prefill.dataCollectionPct,
        repeatSalesPct: prefill.repeatSalesPct,
      }));
    }
  }, [country, industry, setBusinessInputs]);

  /* ─── AOV placeholder based on country ─── */
  const aovPlaceholder = useMemo(() => {
    const sym = getCurrencySymbol(country);
    return `e.g. ${sym}1,200`;
  }, [country]);

  /* ─── Validation ─── */
  const isValid = useMemo(() => {
    return (
      monthlyOrders !== null && monthlyOrders > 0 &&
      aov !== null && aov > 0 &&
      country !== '' &&
      industry !== '' &&
      dataCollectionPct >= 1 && dataCollectionPct <= 100 &&
      repeatSalesPct >= 1 && repeatSalesPct <= 100
    );
  }, [monthlyOrders, aov, country, industry, dataCollectionPct, repeatSalesPct]);

  return (
    <div className="flex flex-col items-center" data-testid="inputs-screen">
      <div className="w-full rounded-2xl" style={glassCard}>
        <div className="px-6 py-8 sm:px-10 sm:py-10">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-xs font-semibold text-[#3b82f6] tracking-wide uppercase mb-3"
          >
            Step 1 of 2 — Business context
          </motion.p>

          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-2xl font-bold text-[#0f172a] mb-2"
          >
            Tell us about your business
          </motion.h2>

          {/* Subhead */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-sm text-[#64748b] leading-relaxed mb-8"
          >
            These numbers let us calculate your revenue opportunity
            accurately. All inputs stay in your browser — nothing is stored.
          </motion.p>

          {/* ─── Inputs ─── */}
          <div className="flex flex-col gap-6">
            {/* Row: Country + Industry */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#334155] mb-1.5">Country</label>
                <p className="text-[11px] text-[#94a3b8] mb-2">Your primary market — this sets the currency.</p>
                <SelectDropdown
                  value={country}
                  onChange={(val) => update('country', val)}
                  options={COUNTRIES}
                  placeholder="Select country"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#334155] mb-1.5">Industry</label>
                <p className="text-[11px] text-[#94a3b8] mb-2">Select the industry that best describes your business.</p>
                <SelectDropdown
                  value={industry}
                  onChange={(val) => update('industry', val)}
                  options={industryOptions}
                  placeholder="Select your industry"
                />
              </div>
            </div>

            {/* Row: Monthly Orders + AOV */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#334155] mb-1.5">Monthly orders</label>
                <p className="text-[11px] text-[#94a3b8] mb-2">Total orders per month across all channels.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  value={monthlyOrders !== null ? formatNumber(monthlyOrders) : ''}
                  onChange={(e) => update('monthlyOrders', stripToInt(e.target.value))}
                  placeholder="e.g. 45,000"
                  className="w-full h-11 px-3 text-sm font-medium text-[#0f172a] placeholder-[#94a3b8] rounded-lg border border-[#e2e8f0] focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all"
                  style={{ background: 'rgba(255,255,255,0.9)' }}
                  data-testid="input-monthly-orders"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#334155] mb-1.5">Average order value (AOV)</label>
                <p className="text-[11px] text-[#94a3b8] mb-2">Average value of a single customer order ({getCurrencySymbol(country).trim()}).</p>
                <input
                  type="text"
                  inputMode="decimal"
                  value={aov !== null ? aov : ''}
                  onChange={(e) => update('aov', stripToFloat(e.target.value))}
                  placeholder={aovPlaceholder}
                  className="w-full h-11 px-3 text-sm font-medium text-[#0f172a] placeholder-[#94a3b8] rounded-lg border border-[#e2e8f0] focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all"
                  style={{ background: 'rgba(255,255,255,0.9)' }}
                  data-testid="input-aov"
                />
              </div>
            </div>

            {/* Row: Data Collection + Repeat Sales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[13px] font-semibold text-[#334155]">Data collection rate</label>
                  <span className="text-[13px] font-bold text-[#3b82f6]">{dataCollectionPct}%</span>
                </div>
                <p className="text-[11px] text-[#94a3b8] mb-3">% of purchases linked to an identified profile.</p>
                <RangeSlider value={dataCollectionPct} onChange={(val) => update('dataCollectionPct', val)} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[13px] font-semibold text-[#334155]">Repeat sales share</label>
                  <span className="text-[13px] font-bold text-[#3b82f6]">{repeatSalesPct}%</span>
                </div>
                <p className="text-[11px] text-[#94a3b8] mb-3">% of revenue from repeat purchases.</p>
                <RangeSlider value={repeatSalesPct} onChange={(val) => update('repeatSalesPct', val)} />
              </div>
            </div>
          </div>

          {/* ─── Footer: Back + Next ─── */}
          <div className="flex items-center justify-between mt-10">
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 text-sm font-medium text-[#94a3b8] hover:text-[#0f172a] transition-colors cursor-pointer"
              data-testid="inputs-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <motion.button
              onClick={isValid ? onNext : undefined}
              disabled={!isValid}
              whileHover={isValid ? { scale: 1.02 } : {}}
              whileTap={isValid ? { scale: 0.97 } : {}}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full transition-all ${
                isValid
                  ? 'bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white shadow-md hover:shadow-lg cursor-pointer'
                  : 'bg-[#e2e8f0] text-[#94a3b8] cursor-not-allowed'
              }`}
              data-testid="inputs-next-button"
            >
              Next: Start the Audit
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InputsScreen;
